import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';

import { api } from '../api/endpoints';
import { buildUrl } from '../api/client';
import logo from '../assets/brand/maklerpass-logo.svg';
import { ErrorMessage } from '../components/ErrorMessage';
import { FullPageLoader } from '../components/Loader';
import { MutationError } from '../components/MutationError';
import { PrintPortal } from '../components/PrintPortal';
import { DocumentSheet } from '../documents/DocumentSheet';
import { fillTemplate, renderText } from '../documents/render';
import { SignaturePad } from './SignaturePad';
import { SignatureBlock } from './SignatureBlock';

// SignPage: Kunden lesen und unterschreiben ein Dokument ohne Anmeldung (Link aus der E-Mail oder auf dem Gerät des
// Maklers). Nach der Unterschrift: Ansicht mit Signaturprotokoll, Drucken bzw. als PDF speichern.
export function SignPage() {
  const { token = '' } = useParams();
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['public-signature', token], queryFn: ({ signal }) => api.publicSignature(token, signal), retry: false });
  const [name, setName] = useState<string | null>(null);
  const [png, setPng] = useState<string | null>(null);
  const [agree, setAgree] = useState(false);
  const [early, setEarly] = useState(false);
  const [printing, setPrinting] = useState(false);
  const donePrinting = useCallback(() => setPrinting(false), []);
  const sign = useMutation({
    mutationFn: () => api.signPublic(token, { name: (name ?? q.data?.signer_name ?? '').trim(), signature: png ?? '', agree, early_start: early }),
    onSuccess: (d) => queryClient.setQueryData(['public-signature', token], d),
  });

  if (q.isPending) return <FullPageLoader label="Dokument wird geladen …" />;
  if (q.isError)
    return (
      <main className="sign-page">
        <img className="sign-logo" src={logo} alt="Maklerpass" />
        <ErrorMessage error={q.error} />
      </main>
    );
  const d = q.data;
  const text = fillTemplate(d.body, d.context, d.field_defs, d.field_values);
  const signed = d.status === 'SIGNED';
  const sheet = (
    <DocumentSheet context={d.context}>
      {renderText(text)}
      {signed && (
        <SignatureBlock
          name={d.signed_name ?? ''}
          signedAt={d.signed_at}
          ip={d.signed_ip}
          contentHash={d.content_hash}
          imageUrl={d.has_signature ? buildUrl(`/public/signatures/${encodeURIComponent(token)}/image`) : null}
          earlyStart={d.options.early_start === true}
        />
      )}
    </DocumentSheet>
  );
  return (
    <main className="sign-page">
      <header className="sign-header">
        <img className="sign-logo" src={logo} alt="Maklerpass" />
        <h1>{signed ? 'Unterschrieben' : 'Dokument unterschreiben'}</h1>
        <p className="muted">{d.title}</p>
      </header>
      {signed ? (
        <div className="alert alert-success" role="status">
          Vielen Dank – unterschrieben am {new Date(d.signed_at ?? '').toLocaleString('de-DE')}. Sie können das Dokument drucken oder als PDF speichern.{' '}
          <button type="button" className="btn btn-small btn-primary" onClick={() => setPrinting(true)}>
            Drucken / PDF
          </button>
        </div>
      ) : (
        <div className="card no-print" style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <strong>1. Dokument prüfen → 2. Optionen wählen → 3. Unten unterschreiben</strong>
            <p className="muted" style={{ margin: '2px 0 0' }}>
              Bitte lesen Sie das Dokument vollständig. Sie können direkt digital unterschreiben oder es als PDF speichern/ausdrucken.
            </p>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-small" onClick={() => setPrinting(true)}>
              Drucken / PDF speichern
            </button>
            <a className="btn btn-small btn-primary" href="#sign-pad-section">
              Weiter zur Unterschrift ↓
            </a>
          </div>
        </div>
      )}
      <div className="sign-document">{sheet}</div>
      {!signed && (
        <section id="sign-pad-section" className="card sign-form" aria-label="Unterschrift">
          <label className="field">
            <span>Ihr vollständiger Name</span>
            <input value={name ?? d.signer_name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </label>
          <SignaturePad onChange={setPng} />
          {d.offer_early_start && (
            <label className="checkbox">
              <input type="checkbox" checked={early} onChange={(e) => setEarly(e.target.checked)} /> Ich verlange ausdrücklich, dass der Makler vor Ablauf der
              Widerrufsfrist mit seiner Tätigkeit beginnt. Mir ist bekannt, dass ich bei einem Widerruf Wertersatz für bereits erbrachte Leistungen schulde.
            </label>
          )}
          <label className="checkbox">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> Ich habe das Dokument gelesen und unterschreibe es
            elektronisch.
          </label>
          {sign.isError && <MutationError error={sign.error} />}
          <button type="button" className="btn btn-primary" disabled={!png || !agree || sign.isPending} onClick={() => sign.mutate()}>
            Verbindlich unterschreiben
          </button>
          <p className="muted small">
            Gespeichert werden Ihr Name, das Unterschriftsbild, Datum und Uhrzeit, Ihre IP-Adresse und eine Prüfsumme des Dokuments (Signaturprotokoll). Der
            Link ist bis {new Date(d.expires_at).toLocaleDateString('de-DE')} gültig.
          </p>
        </section>
      )}
      {printing && (
        <PrintPortal bodyClass="printing-doc" rootClass="doc-print-root" onDone={donePrinting}>
          {sheet}
        </PrintPortal>
      )}
    </main>
  );
}
